import { Component, Input, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { VentaRapidaUser } from 'src/app/models/venta-rapida/venta-rapida-user.model';
import { AnonymousIntent } from 'src/app/models/venta-rapida/anonymous-intent.model';
import { Product } from 'src/app/models/core/product.model';
import { Ticket } from 'src/app/models/core/ticket.model';
import { Event } from 'src/app/models/core/event.model';
import { Seller } from 'src/app/models/core/seller.model';
import { VentaRapidaAssignService } from 'src/app/services/venta-rapida/venta-rapida-assign.service';
import {
  VentaRapidaAssignRequest,
  VentaRapidaAssignedTicket,
} from 'src/app/models/venta-rapida/venta-rapida-assign.model';
import { VentaRapidaCloneContext } from 'src/app/models/venta-rapida/venta-rapida-clone-context.model';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { ShareDirectTicketModalComponent } from '../share-direct-ticket-modal/share-direct-ticket-modal.component';

@Component({
  selector: 'app-venta-rapida-wizard-modal',
  templateUrl: './venta-rapida-wizard-modal.component.html',
  styleUrls: ['./venta-rapida-wizard-modal.component.scss'],
  standalone: false
})
export class VentaRapidaWizardModalComponent {
  @Input() eventId!: string;
  @Input() products: Product[] = [];

  currentStep = signal<number>(1);
  selectedUser = signal<VentaRapidaUser | null>(null);
  anonymousIntent = signal<AnonymousIntent | null>(null);
  selectedProduct = signal<Product | null>(null);
  submitting = signal<boolean>(false);
  lastAmount = signal<number>(0);

  public platform = Capacitor.getPlatform();

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly assignService: VentaRapidaAssignService,
    private readonly toast: CustomToastService,
    private readonly translate: TranslateService,
    private readonly logger: NGXLogger,
  ) {}

  onUserSelected(user: VentaRapidaUser): void {
    this.selectedUser.set(user);
    this.anonymousIntent.set(null);
    this.advanceAfterAssigneePicked();
  }

  onAnonymousChosen(intent: AnonymousIntent): void {
    this.anonymousIntent.set(intent);
    this.selectedUser.set(null);
    this.advanceAfterAssigneePicked();
  }

  private advanceAfterAssigneePicked(): void {
    if (this.products.length === 1) {
      this.selectedProduct.set(this.products[0]);
      this.currentStep.set(3);
      return;
    }
    this.currentStep.set(2);
  }

  onProductSelected(product: Product): void {
    this.selectedProduct.set(product);
    this.currentStep.set(3);
  }

  resetSelection(): void {
    this.selectedUser.set(null);
    this.anonymousIntent.set(null);
    this.selectedProduct.set(null);
    this.currentStep.set(1);
  }

  breadcrumbLabel(): string {
    const user = this.selectedUser();
    if (user) return user.fullname;
    const intent = this.anonymousIntent();
    if (!intent) return '';
    if (intent.kind === 'full-anonymous') return 'Anónimo';
    return intent.value;
  }

  private buildAssignRequest(amount: number): VentaRapidaAssignRequest | null {
    const product = this.selectedProduct();
    if (!product) return null;

    const base: VentaRapidaAssignRequest = {
      event_id: Number(this.eventId),
      product_id: product.id,
      amount,
      quantity: 1,
      source: 'TQR-mobile',
    };

    const user = this.selectedUser();
    if (user) {
      return { ...base, user_id: user.id };
    }

    const intent = this.anonymousIntent();
    if (intent?.kind === 'name') return { ...base, fullname: intent.value };
    if (intent?.kind === 'dni') return { ...base, dni: intent.value };
    return base;
  }

  onGenerate(amount: number): void {
    if (this.submitting()) return;
    this.lastAmount.set(amount);
    const request = this.buildAssignRequest(amount);
    if (!request) return;

    const product = this.selectedProduct();
    if (!product) return;

    this.submitting.set(true);
    this.assignService.assign(request).subscribe({
      next: async (response) => {
        this.submitting.set(false);
        const assigned = response.tickets?.[0];
        if (!assigned) {
          this.toast.show(
            this.translate.instant('venta-rapida.price-step.generate-error'),
            3000,
            'error',
          );
          return;
        }
        const ticket = this.mapAssignedToTicket(assigned, product);
        await this.openShareModal(ticket, assigned.assistant_uuid);
      },
      error: (err) => {
        this.logger.error('[VentaRapidaWizard] assign failed', err);
        this.toast.show(
          this.translate.instant('venta-rapida.price-step.generate-error'),
          3000,
          'error',
        );
        this.submitting.set(false);
      },
    });
  }

  private mapAssignedToTicket(assigned: VentaRapidaAssignedTicket, product: Product): Ticket {
    return {
      id: assigned.ticket_id,
      uuid: assigned.uuid,
      reference: '',
      status: '',
      payment_status: false,
      payment_method: '',
      product,
      seller: {} as Seller,
      event: product.event ?? ({} as Event),
      assistants_count: 0,
      quota: assigned.quantity,
    };
  }

  private async openShareModal(ticket: Ticket, assistantUuid: string): Promise<void> {
    await this.modalCtrl.dismiss();

    const product = this.selectedProduct();
    const ventaRapidaContext: VentaRapidaCloneContext | undefined = product ? {
      eventId: this.eventId,
      amount: this.lastAmount(),
      product,
      user: this.selectedUser(),
      anonymousIntent: this.anonymousIntent(),
      assistantUuid,
    } : undefined;

    const modal = await this.modalCtrl.create({
      component: ShareDirectTicketModalComponent,
      componentProps: { ticket, ventaRapidaContext },
      breakpoints: [0, 0.5, 0.75, 0.95],
      initialBreakpoint: 0.65,
      cssClass: 'custom-modal-options',
      handle: true,
    });
    await modal.present();
  }

  close(): void {
    this.modalCtrl.dismiss();
  }
}
